package com.karyadina.cloudsync.service

import com.karyadina.cloudsync.model.BackgroundSyncConfig
import com.karyadina.cloudsync.model.SyncStatus
import com.karyadina.cloudsync.util.TimeUtil
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow

/**
 * Simple implementation of CloudSyncService
 */
class CloudSyncServiceImpl(
    private val googleAuthService: GoogleAuthService,
    private val syncEngine: SyncEngine,
    private val backgroundSyncService: IBackgroundSyncService,
    private val subscriptionService: com.karyadina.service.SubscriptionService
) : CloudSyncService {
    
    private val _syncEnabled = MutableStateFlow(false)
    override val syncEnabled: StateFlow<Boolean> = _syncEnabled.asStateFlow()
    
    private val _syncStatus = MutableStateFlow<SyncStatus>(SyncStatus.Idle)
    override val syncStatus: StateFlow<SyncStatus> = _syncStatus.asStateFlow()
    
    private val _pendingSyncSize = MutableStateFlow(0.0)
    override val pendingSyncSize: StateFlow<Double> = _pendingSyncSize.asStateFlow()
    
    private val _lastSyncTime = MutableStateFlow(0L)
    override val lastSyncTime: StateFlow<Long> = _lastSyncTime.asStateFlow()
    
    private var backgroundSyncConfig = BackgroundSyncConfig(enabled = false, intervalMinutes = 15)

    private val PREF_SYNC_ENABLED = "pref_cloud_sync_enabled"
    private val PREF_LAST_SYNC_TIME = "pref_last_sync_time"

    init {
        val storage = com.karyadina.service.AppStorage.get()
        _syncEnabled.value = storage.loadBoolean(PREF_SYNC_ENABLED, false)
        _lastSyncTime.value = storage.loadString(PREF_LAST_SYNC_TIME)?.toLongOrNull() ?: 0L
        if (_lastSyncTime.value > 0) {
            _syncStatus.value = SyncStatus.Success(_lastSyncTime.value)
        }
    }
    
    override suspend fun enableSync(): Result<Unit> {
        _syncEnabled.value = true
        com.karyadina.service.AppStorage.get().saveBoolean(PREF_SYNC_ENABLED, true)
        return Result.success(Unit)
    }
    
    override suspend fun disableSync(): Result<Unit> {
        _syncEnabled.value = false
        com.karyadina.service.AppStorage.get().saveBoolean(PREF_SYNC_ENABLED, false)
        _syncStatus.value = SyncStatus.Idle
        return Result.success(Unit)
    }
    
    override suspend fun syncNow(): Result<Unit> {
        _syncStatus.value = SyncStatus.Syncing
        val result = syncEngine.executeSync()
        if (result.isSuccess) {
            val now = TimeUtil.currentTimeMillis()
            _lastSyncTime.value = now
            _syncStatus.value = SyncStatus.Success(now)
            com.karyadina.service.AppStorage.get().saveString(PREF_LAST_SYNC_TIME, now.toString())
        } else {
            val errorMsg = result.exceptionOrNull()?.message ?: "Sync failed"
            _syncStatus.value = SyncStatus.Error(errorMsg)
            println("CloudSync ERROR: $errorMsg")
            result.exceptionOrNull()?.printStackTrace()
        }
        return result.map { Unit }
    }


    
    override suspend fun configureBackgroundSync(config: BackgroundSyncConfig): Result<Unit> {
        backgroundSyncConfig = config
        return Result.success(Unit)
    }
    
    override fun getLastSyncTime(): Long = _lastSyncTime.value
    
    override fun getSyncStatus(): SyncStatus = _syncStatus.value
    
    override suspend fun checkPendingSizeLimit(limitMiB: Double): Boolean {
        return _pendingSyncSize.value <= limitMiB
    }
    
    override fun getBackgroundSyncConfig(): BackgroundSyncConfig = backgroundSyncConfig
}
